import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  Input,
} from '@angular/core';
import { getDownloadURL, ref, Storage } from '@angular/fire/storage';
import { MatIcon } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  NgCommonModule,
  UserIconComponent,
} from '@principle-theorem/ng-shared';
import {
  ISchedulingEventDisplay,
  SchedulingEvent,
  SchedulingEventSummary,
} from '@principle-theorem/principle-core';
import {
  IAppointment,
  IPartnerApp,
  IPatient,
  ISchedulingEventSnapshotSummary,
  ISchedulingEventSummary,
  IStaffer,
  ITreatmentCategory,
  ITreatmentStep,
  MentionResourceType,
  PrincipleCreatedBy,
  SchedulingEventType,
} from '@principle-theorem/principle-core/interfaces';
import {
  asDocRef,
  asReffable,
  DAY_MONTH_YEAR_FORMAT,
  DocumentReference,
  Firestore,
  ISODateType,
  SystemActors,
  TIME_FORMAT,
  WithRef,
} from '@principle-theorem/shared';
import {
  BehaviorSubject,
  combineLatest,
  Observable,
  of,
  ReplaySubject,
} from 'rxjs';
import { map } from 'rxjs/operators';
import { NavigationModule } from '../../../navigation/navigation.module';
import { GlobalStoreService } from '../../../store/global-store.service';
import { TreatmentCategoryIndicatorComponent } from '../../treatment-category-indicator/treatment-category-indicator.component';
import { SchedulingEventSnapshotComponent } from '../scheduling-event-snapshot/scheduling-event-snapshot.component';

@Component({
  selector: 'pr-scheduling-event-display',
  templateUrl: './scheduling-event-display.component.html',
  styleUrls: ['./scheduling-event-display.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgCommonModule,
    MatIcon,
    MatTooltipModule,
    NavigationModule,
    SchedulingEventSnapshotComponent,
    TreatmentCategoryIndicatorComponent,
    UserIconComponent,
  ],
})
export class SchedulingEventDisplayComponent {
  private _storage = inject(Storage);
  private _globalStore = inject(GlobalStoreService);
  readonly dateFormat = DAY_MONTH_YEAR_FORMAT;
  readonly timeFormat = TIME_FORMAT;
  schedulingEventSummary$ = new ReplaySubject<WithRef<ISchedulingEventSummary>>(
    1
  );
  day$ = new BehaviorSubject<ISODateType | undefined>(undefined);
  display$: Observable<ISchedulingEventDisplay>;
  appointment$: Observable<WithRef<IAppointment>>;
  patient$: Observable<WithRef<IPatient>>;
  treatmentStep$: Observable<WithRef<ITreatmentStep>>;
  treatmentCategory$: Observable<
    DocumentReference<ITreatmentCategory> | undefined
  >;
  unknown = SystemActors.Unknown;
  withBackground = input(false, {
    transform: (value) => coerceBooleanProperty(value),
  });

  @Input()
  set schedulingEventSummary(
    schedulingEventSummary: WithRef<ISchedulingEventSummary>
  ) {
    if (schedulingEventSummary) {
      this.schedulingEventSummary$.next(schedulingEventSummary);
    }
  }

  @Input()
  set day(day: ISODateType) {
    this.day$.next(day);
  }

  constructor() {
    this.display$ = this.schedulingEventSummary$.pipe(
      map((schedulingEventSummary) =>
        SchedulingEvent.getDisplay(schedulingEventSummary.eventType)
      )
    );
  }

  getActionLabel(
    schedulingEventSummary: WithRef<ISchedulingEventSummary>
  ): string {
    return SchedulingEventSummary.getActionlabel(schedulingEventSummary);
  }

  getReasonName(
    schedulingEventSummary: WithRef<ISchedulingEventSummary>
  ): string {
    return (
      schedulingEventSummary.reasonName ??
      SchedulingEventSummary.getActionlabel(schedulingEventSummary)
    );
  }

  async getIcon(scheduledBy?: PrincipleCreatedBy): Promise<string | undefined> {
    if (scheduledBy?.type !== MentionResourceType.PartnerApp) {
      return;
    }

    const partnerApp = await Firestore.safeGetDoc(
      asDocRef<IPartnerApp>(scheduledBy.ref)
    );
    const iconUrl = partnerApp?.appIconUrl;
    if (!iconUrl) {
      return;
    }

    const logoRef = ref(this._storage, iconUrl);
    return getDownloadURL(logoRef);
  }

  eventTypeForDay$(
    snapshot?: ISchedulingEventSnapshotSummary
  ): Observable<SchedulingEventType | undefined> {
    return combineLatest([this.schedulingEventSummary$, this.day$]).pipe(
      map(([summary, day]) =>
        !day || snapshot?.date === day ? summary.eventType : undefined
      )
    );
  }

  getSchedulerImage$(
    scheduledBy?: PrincipleCreatedBy
  ): Observable<string | undefined> {
    if (scheduledBy?.type !== MentionResourceType.Staffer) {
      return of(undefined);
    }

    return this._globalStore.getStafferImage$(
      asReffable(asDocRef<IStaffer>(scheduledBy.ref))
    );
  }
}
